import { useCallback } from 'react';
import { ApiError, type JsonValue } from './client';

export type ErrorInput = ApiError | Error | { message?: string } | string | null | undefined;

const stripErrorIdSuffix = (message: string): string =>
  message.replace(/\s*\(Error ID: [^)]+\)/gi, '').trim();

type ValidationIssue = {
  instancePath?: string;
  keyword?: string;
  message?: string;
  params?: JsonValue;
};

const isValidationIssue = (value: JsonValue): value is ValidationIssue => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return true;
};

const toSentenceLabel = (value: string): string => {
  const spacedValue = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (spacedValue.length === 0) return 'Field';
  return spacedValue.charAt(0).toUpperCase() + spacedValue.slice(1);
};

const getValidationPathSegments = (instancePath: string | undefined): string[] =>
  typeof instancePath === 'string'
    ? instancePath
        .split('/')
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0)
    : [];

const formatValidationIssue = (issue: ValidationIssue): string | null => {
  const pathSegments = getValidationPathSegments(issue.instancePath);
  const [group, field] = pathSegments;

  if (group === 'statusAliases' && field && issue.keyword === 'too_small') {
    return `${toSentenceLabel(field)} status aliases cannot be empty.`;
  }

  if (group === 'headerAliases' && field && issue.keyword === 'too_small') {
    return `${toSentenceLabel(field)} header aliases cannot be empty.`;
  }

  if (issue.keyword === 'too_small' && field) {
    return `${toSentenceLabel(field)} must include at least one value.`;
  }

  if (typeof issue.message === 'string' && issue.message.length > 0 && field) {
    return `${toSentenceLabel(field)}: ${issue.message}`;
  }

  if (typeof issue.message === 'string' && issue.message.length > 0) {
    return issue.message;
  }

  return null;
};

const getValidationErrorMessage = (details: JsonValue | undefined): string | null => {
  if (!Array.isArray(details)) return null;
  const firstIssue = details.find(isValidationIssue);
  if (!firstIssue) return null;
  return formatValidationIssue(firstIssue);
};

export const getErrorMessage = (err: unknown): string => {
  if (!err) return 'Unknown error';
  if (err instanceof ApiError) {
    if (err.code === 'VALIDATION_ERROR') {
      const validationMessage = getValidationErrorMessage(err.details);
      if (validationMessage) return validationMessage;
    }
    return stripErrorIdSuffix(err.message);
  }
  if (err instanceof Error) return stripErrorIdSuffix(err.message);
  if (typeof err === 'string') return stripErrorIdSuffix(err);
  if (typeof err === 'number' || typeof err === 'boolean') return String(err);
  if (typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string') return stripErrorIdSuffix(message);
  }
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
};

export const useApiErrorMessage = (): ((err: unknown) => string) =>
  useCallback((err: unknown): string => getErrorMessage(err), []);
